// DO NOT CHANGE ANYTHING (RENAME)
import React, { JSX } from 'react';
import { FeatureAppDefinition } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import App from './App';
import reportWebVitals from '../report-web-vitals';
import { Initialise, initialise, miscTracking } from '@mtnkente/gtm';
import { Metric } from 'web-vitals';
import { SharedDataJson } from './services/DataSharingService';
import DataSharingProvider from './hooks/DataSharingContext';
import { NavigationServiceV1 } from './services/NavigationService';
import { TranslationServiceProvider } from '../localization/provider/TranslationServiceProvider';
import { I18nextProvider } from 'react-i18next';
import i18n from '../localization/internationalization';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { FeatureAppInstanceManager } from './feature-app-instance-manager';
import packageJson from '../../package.json';

function errorFallback({ error }: { error: Error }): JSX.Element | null {
  console.error('🚨 Feature App Error Boundary caught error:', error);
  return null;
}

/**
 * Initialize external services (Datadog, GTM) only once.
 * This prevents duplicate initialization and potential conflicts.
 */
function initializeExternalServices(): void {
  try {
    // Initialize Datadog RUM
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID ?? '',
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN ?? '',
      site:
        (process.env.REACT_APP_DATADOG_SITE as import('@datadog/browser-core').Site) ??
        'datadoghq.eu',
      service: 'read-pwa',
      env: process.env.REACT_APP_DATADOG_ENV ?? '',
      version: packageJson.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      enablePrivacyForActionName: true,
    });

    // Initialize GTM
    initialise(
      new Initialise({
        consentAds: true,
        consentAnalytics: true,
        consentPreferences: true,
        gtmId: 'gtm_token',
      })
    );

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('✅ External services initialized successfully');
    }
  } catch (error) {
    console.error('❌ Failed to initialize external services:', error);
    // Don't throw - we want the app to continue even if services fail
  }
}

const FeatureHubAppDefinition: FeatureAppDefinition<ReactFeatureApp> = {
  dependencies: {
    featureServices: {
      'api:get-shared-data': '1.0.0',
      'shell:navigation-service': '^1.0.0',
    },
  },

  /**
   * Feature Hub lifecycle hook - called when the feature app is created
   */
  create: ({ featureServices, config }) => {
    const manager = FeatureAppInstanceManager.getInstance();

    if (process.env.NODE_ENV === 'development') {
      const initInfo = manager.getInitializationInfo();
      // eslint-disable-next-line no-console
      console.log('🚀 FeatureHubAppDefinition create called', {
        timestamp: Date.now(),
        isInstanceCreated: manager.isInstanceCreated(),
        servicesInitialized: manager.areServicesInitialized(),
        instanceAge: initInfo.age,
      });
    }

    // ✅ PROPER SOLUTION: Check if instance already exists
    if (manager.isInstanceCreated()) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('🔄 Returning existing app instance to prevent double rendering');
      }
      // Return a NO-OP render function to prevent React Hook errors
      return {
        render: (): null => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('🚫 BLOCKED: Second render prevented to avoid React Hook errors');
          }
          return null; // Return null to prevent rendering
        },
      };
    }

    const sharedDataService = featureServices['api:get-shared-data'] as SharedDataJson;
    const navigationService = featureServices['shell:navigation-service'] as NavigationServiceV1;

    // Initialize services only once
    if (!manager.areServicesInitialized()) {
      initializeExternalServices();
      manager.markServicesAsInitialized();
    }

    const appInstance: ReactFeatureApp = {
      render: (): JSX.Element => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('🎨 FeatureHubAppDefinition render', { timestamp: Date.now() });
        }

        return (
          <ErrorBoundary fallback={errorFallback}>
            <I18nextProvider i18n={i18n}>
              <TranslationServiceProvider>
                <DataSharingProvider value={sharedDataService}>
                  <App config={config} navigationService={navigationService} />
                </DataSharingProvider>
              </TranslationServiceProvider>
            </I18nextProvider>
          </ErrorBoundary>
        );
      },
    };

    // Store the instance using the manager
    manager.setAppInstance(appInstance);

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('✅ Created new app instance', { timestamp: Date.now() });
    }

    return appInstance;
  },
};

const sendToAnalytics = ({ id, name, value }: Metric): void => {
  // Assumes the metric name is the same as the event name in Google Analytics
  miscTracking('webVitals', 'reportWebVitals', {
    eventCategory: 'Web Vitals',
    value: Math.round(name === 'CLS' ? value * 1000 : value), // Ensure values are in milliseconds
    eventLabel: id, // Use the Web Vitals ID as the event label
    nonInteraction: true, // Prevents this event from affecting bounce rate
  });
};

// webVital to monitor application performance
reportWebVitals(sendToAnalytics);

// Re-export utility functions for backward compatibility
export { resetFeatureAppManager, getFeatureAppManager } from './feature-app-instance-manager';

export default FeatureHubAppDefinition;
